/**
 * After hours (trust audit Phase 1; Corbin 10/22; mockup AsHAGZ2vsJr3FFhVuW4Mib, D).
 *
 * One line on the job page: is this job after-hours, and why. Auto follows the
 * booking (before 8:00 AM or from 5:00 PM Central on a weekday, or a weekend)
 * or the quote's after-hours box. Never the tech's start time. Yes / No is the
 * office override and says who set it. After-hours work pays 1.5x points.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { HowItWorks } from "@/components/how-it-works";

export interface AfterHoursState {
    afterHours: boolean;
    source: "office" | "quote" | "booking" | "none";
    detail: string;
    override: boolean | null;
}

type Choice = "auto" | "yes" | "no";

export function choiceOf(override: boolean | null): Choice {
    return override === true ? "yes" : override === false ? "no" : "auto";
}

export function AfterHoursLine({
    state,
    canManage,
    pending,
    onPick,
}: {
    state: AfterHoursState;
    canManage: boolean;
    pending?: boolean;
    onPick: (value: boolean | null) => void;
}) {
    const current = choiceOf(state.override);
    const choices: Array<{ key: Choice; label: string; value: boolean | null }> = [
        { key: "auto", label: "Auto", value: null },
        { key: "yes", label: "Yes", value: true },
        { key: "no", label: "No", value: false },
    ];
    return (
        <div
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm"
            data-testid="after-hours-line"
        >
            <div className="min-w-0">
                <span className="flex items-center gap-2 font-semibold">
                    After hours: {state.afterHours ? "Yes" : "No"}
                    <HowItWorks
                        title="How after hours works"
                        testId="how-after-hours"
                        steps={[
                            {
                                title: "Auto follows the booking",
                                text: "A job booked to start before 8:00 AM or at 5:00 PM or later on a weekday, or on a weekend, is after hours. So is a job whose quote has the after-hours box. When the tech actually started never counts.",
                                picture: "after-hours.png",
                                alt: "The After hours line with Auto, Yes and No ringed",
                            },
                            {
                                title: "Yes or No overrides it",
                                text: "Tap Yes or No when the booking doesn't tell the truth. It says who set it. Tap Auto to go back.",
                            },
                            {
                                title: "What it changes",
                                text: "After-hours work pays the tech 1.5× points. Points already earned are not redone.",
                            },
                        ]}
                    />
                </span>
                <span className="block text-xs text-foreground/70">
                    {state.detail}
                    {state.afterHours ? " · points pay 1.5×" : ""}
                </span>
            </div>
            {canManage && (
                <div
                    className="inline-flex overflow-hidden rounded-md border border-border"
                    role="group"
                    aria-label="After hours"
                >
                    {choices.map((c) => (
                        <button
                            key={c.key}
                            type="button"
                            aria-pressed={current === c.key}
                            disabled={pending}
                            onClick={() => current !== c.key && onPick(c.value)}
                            className={cn(
                                "px-3 py-1 text-xs font-semibold",
                                current === c.key ? "bg-foreground text-background" : "bg-background hover:bg-muted",
                            )}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export function AfterHoursCard({ jobId, canManage }: { jobId: string; canManage: boolean }) {
    const qc = useQueryClient();
    const key = ["job", jobId, "after-hours"];
    const q = useQuery({
        queryKey: key,
        queryFn: () => api.get<AfterHoursState>(`/api/jobs/${jobId}/after-hours`),
    });
    const set = useMutation({
        mutationFn: (value: boolean | null) => api.post<AfterHoursState>(`/api/jobs/${jobId}/after-hours`, { value }),
        onSuccess: (data) => {
            qc.setQueryData(key, data);
            toast.success(
                data.override === null
                    ? `After hours is back on Auto: ${data.afterHours ? "Yes" : "No"}.`
                    : `After hours set to ${data.afterHours ? "Yes" : "No"}.`,
            );
        },
        onError: () => toast.error("Couldn't change after hours. Try again."),
    });
    if (!q.data) return null;
    return (
        <AfterHoursLine state={q.data} canManage={canManage} pending={set.isPending} onPick={(v) => set.mutate(v)} />
    );
}
